/**
 * Shared query factory used by multiple features (import, media-units,
 * availability-import).
 *
 * Rules:
 * - Each query key must be defined in exactly one canonical location.
 * - Features MUST import from here instead of redefining the same key.
 */
import { suppliers } from '@ooh/contracts';
import type { EndpointDef, callEndpoint } from '@ooh/contracts';

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

/** Cache key for the full suppliers list — see features/suppliers/api/queries.ts's supplierKeys.all. */
export const SUPPLIERS_ALL_KEY = ['suppliers'] as const;

/**
 * Canonical query factory for GET /suppliers.
 * Shared between features/import and features/media-units.
 * Query key is SUPPLIERS_ALL_KEY — the single source of truth so that
 * mutations invalidating supplierKeys.all also invalidate this query.
 *
 * Returns SupplierListItem[] (plain array) — the API returns a plain array,
 * not a wrapped {data:[...]} object. Consumers must NOT access .data on the result.
 */
export function suppliersQuery(auth: WithCallWithAuth) {
  return {
    queryKey: SUPPLIERS_ALL_KEY,
    queryFn: () => auth.callWithAuth(suppliers.list, {}),
  };
}
